import { useTranslation } from 'react-i18next';
import type { Detention, LocationWait } from '../../api/types';
import { SectionCard } from '../../components/ui/SectionCard';
import { StatusPill } from '../../components/ui/StatusPill';
import { StatusIcon } from '../../components/ui/StatusIcon';
import { formatStopMinutes } from './stopLabels';
import styles from './DetentionCard.module.css';

/**
 * Detention (dnm-work#165 C3, rhino-api R52): pickup wait, drop wait, customer stops and the total,
 * all in server minutes. The console only formats them (P3): nothing is added or subtracted here.
 * Hidden when the server sends null (non-ADMIN, or the truck has not arrived anywhere yet).
 */
export function DetentionCard({ detention }: { detention: Detention | null | undefined }) {
  const { t } = useTranslation();
  if (!detention) return null;
  const title = t('stops.detention.title');
  const badge = detention.final ? (
    <StatusPill
      bg="var(--status-green-bg)"
      text="var(--status-green-text)"
      label={t('stops.detention.final')}
      icon={<StatusIcon name="check" size={14} />}
    />
  ) : (
    <StatusPill
      bg="var(--status-amber-bg)"
      text="var(--status-amber-text)"
      label={t('stops.detention.soFar')}
      icon={<StatusIcon name="hourglass" size={14} />}
    />
  );

  const wait = (label: string, w: LocationWait | null) => (
    <div className={styles.row}>
      <span className={styles.label}>{label}</span>
      {w ? (
        <>
          <span className={styles.value}>{formatStopMinutes(t, w.detentionMinutes)}</span>
          <span className={styles.detail}>
            {t('stops.detention.waitDetail', {
              wait: formatStopMinutes(t, w.waitMinutes),
              excluded: formatStopMinutes(t, w.excludedMinutes),
            })}
          </span>
        </>
      ) : (
        <span className={styles.detail}>{t('stops.detention.notArrived')}</span>
      )}
    </div>
  );

  return (
    <section aria-label={title} className={styles.wrap}>
      <SectionCard title={title} action={badge}>
        <div className={styles.body}>
          <div className={styles.rows}>
            {wait(t('stops.detention.pickup'), detention.pickup)}
            {wait(t('stops.detention.drop'), detention.drop)}
            <div className={styles.row}>
              <span className={styles.label}>{t('stops.detention.customerStops')}</span>
              <span className={styles.value}>
                {formatStopMinutes(t, detention.customerStopMinutes)}
              </span>
            </div>
            <div className={`${styles.row} ${styles.total}`}>
              <span className={styles.label}>{t('stops.detention.total')}</span>
              <span className={styles.value}>
                {formatStopMinutes(t, detention.totalDetentionMinutes)}
              </span>
            </div>
          </div>
          <div className={styles.foot}>
            {t('stops.detention.freeTime', { hours: detention.freeHours })}
          </div>
        </div>
      </SectionCard>
    </section>
  );
}
